import api from './api';
import type { 
  EmployeeDocument, 
  DocumentFilters, 
  UpdateDocumentPayload 
} from '@/types/employeeDocument';
import type { FormerEmployeeFile } from '@/types/formerEmployee';

export const employeeDocumentService = {
  /**
   * Get all documents for an employee
   */
  async getDocuments(userId: number, filters?: DocumentFilters): Promise<EmployeeDocument[]> {
    try {
      const response = await api.get<{ success: boolean; data: EmployeeDocument[]; total: number }>(
        `/users/${userId}/documents`,
        { params: filters }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching employee documents:', error);
      throw error;
    }
  },

  /**
   * Upload one or multiple documents for an employee
   */
  async uploadDocuments(
    userId: number, 
    formData: FormData, 
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<EmployeeDocument[]> {
    try {
      const response = await api.post<{ success: boolean; message: string; data: EmployeeDocument[] }>(
        `/users/${userId}/documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress,
        }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error uploading employee documents:', error);
      throw error;
    }
  },

  /**
   * Get single document details
   */
  async getDocument(userId: number, documentId: number): Promise<EmployeeDocument> {
    try {
      const response = await api.get<{ success: boolean; data: EmployeeDocument }>(
        `/users/${userId}/documents/${documentId}`
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching document details:', error);
      throw error;
    }
  },

  /**
   * Update document metadata
   */
  async updateDocument(
    userId: number, 
    documentId: number, 
    data: UpdateDocumentPayload
  ): Promise<EmployeeDocument> {
    try {
      const response = await api.put<{ success: boolean; message: string; data: EmployeeDocument }>(
        `/users/${userId}/documents/${documentId}`,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error('Error updating document metadata:', error);
      throw error;
    }
  },

  /**
   * Replace document file
   */
  async replaceDocument(
    userId: number, 
    documentId: number, 
    formData: FormData
  ): Promise<EmployeeDocument> {
    try {
      const response = await api.post<{ success: boolean; message: string; data: EmployeeDocument }>(
        `/users/${userId}/documents/${documentId}/replace`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error replacing document file:', error);
      throw error;
    }
  },

  /**
   * Delete a document
   */
  async deleteDocument(userId: number, documentId: number): Promise<void> {
    try {
      await api.delete(`/users/${userId}/documents/${documentId}`);
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  },

  /**
   * Move a staff document into the linked former-employee archive.
   */
  async archiveDocument(userId: number, documentId: number): Promise<FormerEmployeeFile> {
    const response = await api.post<{ success: boolean; message: string; data: FormerEmployeeFile }>(
      `/users/${userId}/documents/${documentId}/archive`
    );
    return response.data.data;
  },

  /**
   * Download a document
   */
  async downloadDocument(userId: number, documentId: number, filename?: string): Promise<void> {
    try {
      const response = await api.get(`/users/${userId}/documents/${documentId}/download`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `document_${documentId}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  },

  /**
   * Get preview blob URL for viewing inline (PDF or Image)
   */
  async getPreviewBlobUrl(userId: number, documentId: number): Promise<{ url: string; mimeType: string; blob: Blob }> {
    try {
      const response = await api.get(`/users/${userId}/documents/${documentId}/preview`, {
        responseType: 'blob',
      });

      const mimeType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      return { url, mimeType, blob };
    } catch (error) {
      console.error('Error loading preview for document:', error);
      throw error;
    }
  },
};

export default employeeDocumentService;

